import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useApp, optimizedImage, handleImgError } from "../store";
import { Upload, X, Check, AlertCircle, Trash2 } from "lucide-react";

/** Fuzzy score — returns how much of `needle` appears (in order) inside `haystack`.
 *  Both strings are already lowercased/stripped. Higher = better match. */
function fuzzyScore(needle, haystack) {
  if (!needle || !haystack) return 0;
  if (haystack.includes(needle)) return needle.length * 3; // exact-substring bonus
  let score = 0, hi = 0;
  for (const ch of needle) {
    const found = haystack.indexOf(ch, hi);
    if (found !== -1) {
      score += 1;
      hi = found + 1;
    }
  }
  return score;
}

/** Normalise filenames/product names to lowercase alphanumeric only for matching. */
function keyify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function guessProductForFilename(filename, products) {
  const stem = keyify(filename.replace(/\.[^.]+$/, ""));
  if (!stem) return null;
  let best = null, bestScore = 0;
  for (const p of products) {
    const nameKey = keyify(p.name);
    const botanicalKey = keyify(p.botanical_name || "");
    const idKey = keyify(p.id);
    const s = Math.max(
      fuzzyScore(nameKey, stem),
      fuzzyScore(stem, nameKey),
      fuzzyScore(botanicalKey, stem),
      fuzzyScore(stem, botanicalKey),
      stem === idKey ? 100 : 0, // filename == product id
    );
    if (s > bestScore && s >= 3) {
      bestScore = s; best = p;
    }
  }
  return best;
}

export default function BulkImageManager({ onClose }) {
  const { API, authHeaders } = useApp();
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]); // {id, file, productId, status: 'pending'|'uploading'|'saved'|'error', message}
  const [dragOver, setDragOver] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data));
  }, [API]);

  // Products that don't yet have a mapping in the current queue.
  const usedProductIds = useMemo(() => new Set(rows.map(r => r.productId).filter(Boolean)), [rows]);

  const addFiles = useCallback((files) => {
    const arr = Array.from(files || []).filter(f => f.type.startsWith("image/"));
    if (!arr.length) return;
    const next = arr.map((file, idx) => {
      const guess = guessProductForFilename(file.name, products);
      return {
        id: `${Date.now()}-${idx}-${file.name}`,
        file,
        productId: guess?.id || "",
        status: "pending",
        message: "",
      };
    });
    setRows(prev => [...prev, ...next]);
  }, [products]);

  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));
  const setRowStatus = (id, status, message = "") =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, status, message } : r));
  const setRowProduct = (id, productId) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, productId } : r));

  const uploadRow = async (row) => {
    if (!row.productId) {
      setRowStatus(row.id, "error", "No product assigned.");
      return false;
    }
    setRowStatus(row.id, "uploading", "");
    try {
      const fd = new FormData(); fd.append("file", row.file);
      const up = await axios.post(`${API}/upload`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      const newUrl = `${process.env.REACT_APP_BACKEND_URL}${up.data.url}`;
      const updated = await axios.put(`${API}/products/${row.productId}`,
        { image_url: newUrl }, { headers: authHeaders });
      setProducts(prev => prev.map(p => p.id === row.productId ? updated.data : p));
      setRowStatus(row.id, "saved", "");
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Upload failed";
      setRowStatus(row.id, "error", typeof msg === "string" ? msg : "Upload failed");
      return false;
    }
  };

  const uploadAll = async () => {
    if (bulkRunning) return;
    setBulkRunning(true);
    const pending = rows.filter(r => r.status === "pending" || r.status === "error");
    // Upload in parallel batches of 4 to avoid overwhelming the backend.
    const BATCH = 4;
    for (let i = 0; i < pending.length; i += BATCH) {
      const chunk = pending.slice(i, i + BATCH);
      await Promise.all(chunk.map(uploadRow));
    }
    setBulkRunning(false);
  };

  const savedCount = rows.filter(r => r.status === "saved").length;
  const errorCount = rows.filter(r => r.status === "error").length;
  const pendingCount = rows.filter(r => r.status === "pending").length;

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const items = e.dataTransfer?.items;
    if (items) {
      const files = [];
      for (const it of items) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      addFiles(files);
    } else {
      addFiles(e.dataTransfer?.files || []);
    }
  };

  const unmappedProducts = useMemo(
    () => products.filter(p => !usedProductIds.has(p.id)),
    [products, usedProductIds]
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-[110] flex items-start justify-center p-4 overflow-y-auto" data-testid="bulk-image-manager">
      <div className="bg-[#FAF9F6] w-full max-w-7xl my-8 p-8">
        <div className="flex justify-between items-start mb-4 sticky top-0 bg-[#FAF9F6] pb-4 border-b border-[#E5E1D8] z-10">
          <div>
            <div className="overline text-copper mb-1">Image Manager · Fast Bulk Upload</div>
            <h3 className="font-serif-display text-3xl">Drop all photos at once</h3>
            <p className="text-sm text-[#5C5E5C] mt-2 max-w-3xl">
              Drag &amp; drop multiple photos below (or click to select). Filenames are auto-matched to products
              (e.g. <code>ruh-gulab.jpg</code> → Ruh Gulab). Review the assignments, then click <b>Upload all</b> —
              runs 4 uploads in parallel. Every upload is now safely mirrored to persistent storage before success is reported.
            </p>
          </div>
          <button onClick={onClose} data-testid="bulk-close" className="p-2"><X size={22} /></button>
        </div>

        {/* Drop zone */}
        <label
          onDragEnter={e => { e.preventDefault(); setDragOver(true); }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`block border-2 border-dashed cursor-pointer text-center py-14 mb-6 transition-colors ${
            dragOver ? "border-copper bg-[#F3EFE9]" : "border-[#E5E1D8] hover:border-copper"
          }`}
          data-testid="bulk-dropzone"
        >
          <Upload size={32} className="mx-auto mb-3 text-[#8A8D8A]" strokeWidth={1.2} />
          <div className="font-serif-display text-2xl mb-1">Drop photos here</div>
          <div className="text-xs text-[#8A8D8A] mb-3">or click to browse — multiple files supported</div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={e => addFiles(e.target.files)}
            className="hidden"
            data-testid="bulk-file-picker"
          />
          <span className="inline-block border border-copper text-copper px-6 py-2 text-xs overline">Choose files</span>
        </label>

        {/* Queue */}
        {rows.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-copper text-xs">
                {rows.length} photo{rows.length === 1 ? "" : "s"} queued
                {savedCount > 0 && <span className="text-[#3C6348] ml-3">✓ {savedCount} saved</span>}
                {errorCount > 0 && <span className="text-[#9E3C3C] ml-3">✗ {errorCount} failed</span>}
                {pendingCount > 0 && <span className="text-[#8A8D8A] ml-3">{pendingCount} pending</span>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setRows([])}
                  disabled={bulkRunning}
                  className="text-xs text-[#9E3C3C] underline hover:opacity-80 disabled:opacity-40"
                  data-testid="bulk-clear-queue"
                >
                  Clear queue
                </button>
                <button
                  onClick={uploadAll}
                  disabled={bulkRunning || pendingCount === 0}
                  className="btn-primary text-xs disabled:opacity-40"
                  data-testid="bulk-upload-all"
                >
                  {bulkRunning ? "Uploading…" : `Upload all (${pendingCount})`}
                </button>
              </div>
            </div>

            <div className="border border-[#E5E1D8]">
              <div className="grid grid-cols-12 gap-3 text-[10px] overline text-[#8A8D8A] px-3 py-2 border-b border-[#E5E1D8]">
                <div className="col-span-2">Preview</div>
                <div className="col-span-3">Filename</div>
                <div className="col-span-4">Assign to product</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-1"></div>
              </div>
              {rows.map(r => {
                const preview = URL.createObjectURL(r.file);
                return (
                  <div key={r.id} className="grid grid-cols-12 gap-3 items-center px-3 py-2 border-b border-[#E5E1D8] text-sm" data-testid={`bulk-row-${r.id}`}>
                    <div className="col-span-2">
                      <img src={preview} alt={r.file.name} className="w-16 h-16 object-cover" />
                    </div>
                    <div className="col-span-3 truncate text-xs" title={r.file.name}>{r.file.name}</div>
                    <div className="col-span-4">
                      <select
                        value={r.productId}
                        onChange={e => setRowProduct(r.id, e.target.value)}
                        disabled={r.status === "uploading" || r.status === "saved"}
                        className="input-underline w-full text-xs"
                        data-testid={`bulk-row-select-${r.id}`}
                      >
                        <option value="">— Select product —</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name}{r.productId === p.id ? " (auto-matched)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2 text-xs">
                      {r.status === "pending" && <span className="text-[#8A8D8A]">Pending</span>}
                      {r.status === "uploading" && <span className="text-copper">Uploading…</span>}
                      {r.status === "saved" && <span className="text-[#3C6348] flex items-center gap-1"><Check size={12} /> Saved</span>}
                      {r.status === "error" && (
                        <span className="text-[#9E3C3C] flex items-center gap-1" title={r.message}>
                          <AlertCircle size={12} /> {(r.message || "Error").slice(0, 30)}
                        </span>
                      )}
                    </div>
                    <div className="col-span-1 text-right">
                      <button
                        onClick={() => removeRow(r.id)}
                        disabled={r.status === "uploading"}
                        className="text-[#9E3C3C] hover:opacity-80 disabled:opacity-30"
                        data-testid={`bulk-row-remove-${r.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Products awaiting photos */}
        {unmappedProducts.length > 0 && rows.length > 0 && (
          <div className="mb-6 p-4 bg-[#F3EFE9] text-xs text-[#5C5E5C]">
            <b>Still without a queued photo ({unmappedProducts.length}):</b>{" "}
            {unmappedProducts.map(p => p.name).join(" · ")}
          </div>
        )}

        {/* Current product grid — for browsing / individual replace */}
        <div>
          <div className="overline text-xs text-[#8A8D8A] mb-3">Current photos (click any tile to replace individually)</div>
          <div className="grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {products.map(p => (
              <label
                key={p.id}
                className="border border-[#E5E1D8] hover:border-copper p-2 cursor-pointer transition-colors block"
                data-testid={`bulk-quick-${p.id}`}
              >
                <div className="aspect-[4/5] bg-clay overflow-hidden mb-2 relative">
                  {p.image_url ? (
                    <img
                      src={optimizedImage(p.image_url, 300)}
                      alt={p.name}
                      onError={handleImgError(p)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-[#9E3C3C] text-[10px]">
                      <AlertCircle size={22} strokeWidth={1.5} />
                      <span>No image</span>
                    </div>
                  )}
                </div>
                <div className="text-[11px] font-medium leading-tight line-clamp-2">{p.name}</div>
                <div className="text-[9px] text-[#8A8D8A] mt-0.5">{p.category}</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setRows(prev => [...prev, {
                        id: `${Date.now()}-${p.id}`,
                        file: f, productId: p.id, status: "pending", message: "",
                      }]);
                    }
                    e.target.value = "";
                  }}
                  className="hidden"
                />
              </label>
            ))}
          </div>
        </div>

        <div className="sticky bottom-0 bg-[#FAF9F6] border-t border-[#E5E1D8] mt-8 pt-4 flex items-center justify-between">
          <span className="text-sm text-[#5C5E5C]" data-testid="bulk-progress">
            {savedCount > 0 ? `✓ ${savedCount} photo${savedCount === 1 ? "" : "s"} saved` : rows.length ? `${rows.length} queued` : "Ready when you are."}
          </span>
          <button onClick={onClose} className="btn-primary" data-testid="bulk-done">Done</button>
        </div>
      </div>
    </div>
  );
}
